import { IconName } from "@telia/teddy";
import PAGES from "../../../../../../constants/pages";
import { filterRetailerLinks, D2D_AND_TM_HIDDEN_LINKS } from "./filterRetailerLinks";

export const APP_KEYS = {
    "open-pages": "open-pages",
    "web-shop": "web-shop",
    storybook: "storybook",
    "my-pages": "my-pages",
    "my-business": "my-business",
    "my-portal": "my-portal",
    "business-net": "business-net",
    "telia-hub": "telia-hub",
} as const;

export type AppKey = (typeof APP_KEYS)[keyof typeof APP_KEYS];

export type Link = {
    appKey?: AppKey;
    highlightedLinks?: Link[];
    icon?: IconName;
    link: string;
    links?: Link[];
    name: string;
};

export const MY_PAGE_LINKS = {
    name: "Min Side",
    link: "/minside",
    appKey: APP_KEYS["my-pages"],
    links: [
        {
            name: "Hjem",
            link: "/minside/hjem",
            appKey: APP_KEYS["my-pages"],
            icon: "home",
        },
        {
            name: "Profil",
            link: "/minside/profil",
            appKey: APP_KEYS["my-pages"],
            icon: "end-user",
        },
        {
            name: "Faktura",
            link: "/minside/faktura",
            appKey: APP_KEYS["my-pages"],
            icon: "doc",
        },
        {
            name: "Personvern",
            link: "/minside/personvern",
            appKey: APP_KEYS["my-pages"],
            icon: "secured-1",
        },
    ],
} as const satisfies Link;

export const MY_BUSINESS_LINKS = {
    name: "Min bedrift",
    link: "https://beta.minbedrift.telia.no",
    appKey: APP_KEYS["my-business"],
    links: [],
    icon: "home",
} as const satisfies Link;

export const TELIA_HUB_LINKS = {
    name: "Telia Hub",
    link: "https://beta.hub.telia.no/",
    appKey: APP_KEYS["telia-hub"],
    links: [],
    icon: "people-hub",
} as const satisfies Link;

export const MY_PORTAL_LINKS = {
    name: "Min portal",
    link: "https://minportal.telia.no",
    appKey: APP_KEYS["my-portal"],
    links: [],
    icon: "end-user",
} as const satisfies Link;

export const BUSINESS_NET_LINKS = {
    name: "Bedriftsnett",
    link: "https://sb.telia.no",
    appKey: APP_KEYS["business-net"],
    links: [],
    icon: "doc",
} as const satisfies Link;

export const SMART_CONNECT_LINKS = {
    name: "Smart Connect",
    link: "https://teliasmartconnect.com/",
    appKey: APP_KEYS["open-pages"],
    links: [],
    icon: "smart-connect",
} as const satisfies Link;

export const LOGGED_IN_LINKS_BUSINESS = [
    TELIA_HUB_LINKS,
    MY_BUSINESS_LINKS,
    MY_PORTAL_LINKS,
    SMART_CONNECT_LINKS,
    BUSINESS_NET_LINKS,
] as const satisfies Link[];

export const LOGGED_IN_LINKS = [MY_PAGE_LINKS, ...LOGGED_IN_LINKS_BUSINESS] as const satisfies Link[];

export const PRIVATE_LINKS = {
    name: "Privat",
    link: "/",
    appKey: APP_KEYS["open-pages"],
    highlightedLinks: [
        {
            name: "Mobilabonnement",
            link: "/mobilabonnement/priser/",
            appKey: APP_KEYS["web-shop"],
        },
        {
            name: "Mobiltelefoner",
            link: "/mobiltelefoner/",
            appKey: APP_KEYS["web-shop"],
        },
        {
            name: "Varemerker",
            link: "/varemerker/",
            appKey: APP_KEYS["web-shop"],
        },
        {
            name: "Kampanjer",
            link: "/kampanje/",
            appKey: APP_KEYS["open-pages"],
        },
        {
            name: "Faktura",
            link: "/hjelp/faktura/",
            appKey: APP_KEYS["open-pages"],
        },
        {
            name: "Se Telia Play",
            link: "https://www.teliaplay.no",
            appKey: APP_KEYS["open-pages"],
        },
    ],
    links: [
        {
            name: "Logo",
            link: "/",
            appKey: APP_KEYS["open-pages"],
            links: [],
            highlightedLinks: [],
        },
        {
            name: "Nettbutikk",
            link: "/mobiltelefoner/",
            appKey: APP_KEYS["web-shop"],
            highlightedLinks: [
                {
                    name: "Kjøp iPhone",
                    link: "/varemerker/apple/iphone/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Kjøp AirPods",
                    link: "/varemerker/apple/airpods/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Kjøp Samsung Galaxy",
                    link: "/varemerker/samsung/galaxy-telefoner/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Kjøp Google Pixel",
                    link: "/varemerker/google/pixel-telefoner/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Kjøp Like Fin mobil",
                    link: "/mobiltelefoner/like-fin/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Kampanjer",
                    link: "/kampanje/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Mobil med abonnement",
                    link: "/mobilabonnement/mobil-med-abonnement/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Mobilforsikring",
                    link: "/trygghet/tjenester/mobilforsikring/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
            links: [
                {
                    name: "Kjøp mobiltelefon",
                    link: "/mobiltelefoner/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Kjøp smartklokke",
                    link: "/smartklokker/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Kjøp nettbrett",
                    link: "/nettbrett/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Kjøp tilbehør",
                    link: "/tilbehor/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Kjøp mobilt bredbånd-ruter",
                    link: "/rutere/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Pakketilbud",
                    link: "/mobilabonnement/pakketilbud/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Våre varemerker",
                    link: "/varemerker/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Hjelp mobil",
                    link: "/mobilabonnement/hjelp/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
        },
        {
            name: "Mobilabonnement",
            link: "/mobilabonnement/",
            appKey: APP_KEYS["open-pages"],
            highlightedLinks: [
                {
                    name: "Fyll på kontantkort",
                    link: "/mobilabonnement/kontantkort/lade/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Kampanjer",
                    link: "/kampanje/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "SIM, PIN og PUK",
                    link: "/mobilabonnement/sim-kort/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Hjelp til faktura",
                    link: "/hjelp/faktura/faktura-mobil/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Alt om utland",
                    link: "/mobilabonnement/utland/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Data og datapakker",
                    link: "/mobilabonnement/data-og-datapakker/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Trygghetstjenester",
                    link: "/trygghet/tjenester/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Bytte eier",
                    link: "/mobilabonnement/hjelp/bytte-eier/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
            links: [
                {
                    name: "Alle mobilabonnement",
                    link: "/mobilabonnement/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Ubegrenset data",
                    link: "/mobilabonnement/ubegrenset-data/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Mobilabonnement for familie",
                    link: "/mobilabonnement/familie/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Mobilabonnement for barn",
                    link: "/mobilabonnement/barn/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Pakketilbud",
                    link: "/mobilabonnement/pakketilbud/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Kontantkort",
                    link: "/mobilabonnement/kontantkort/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Prisliste",
                    link: "/mobilabonnement/priser/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Hjelp mobil",
                    link: "/mobilabonnement/hjelp/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
        },
        {
            name: "TV og streaming",
            link: "/tv/",
            appKey: APP_KEYS["open-pages"],
            highlightedLinks: [
                {
                    name: "Valgmenyen i Telia Play",
                    link: "https://account.teliaplay.no/subscription/selection-menu",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Aktivere strømmetjenester",
                    link: "/tv/innholdsoversikt/strommetjenester/#registrere",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Kjøp Premier League",
                    link: "/tv/innholdsoversikt/premiumtjenester/premier-league-v-premium/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Kampanjer",
                    link: "/kampanje/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Kom i gang med Telia Play",
                    link: "/tv/hjelp/kom-i-gang/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Slik bruker du Telia Play",
                    link: "/tv/hjelp/daglig-bruk/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Driftsmeldinger og feilsøk",
                    link: "/tv/hjelp/tv-feilsok/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
            links: [
                {
                    name: "Se Telia Play",
                    link: "https://www.teliaplay.no",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Innholdsoversikt",
                    link: "/tv/innholdsoversikt/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Strømmetjenester",
                    link: "/tv/innholdsoversikt/strommetjenester/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Premiumtjenester",
                    link: "/tv/innholdsoversikt/premiumtjenester/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "TV-bokser",
                    link: "/tv/tv-bokser/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Hjelp TV",
                    link: "/tv/hjelp/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Alt om TV",
                    link: "/tv/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
        },
        {
            name: "Internett",
            link: "/internett/",
            appKey: APP_KEYS["open-pages"],
            highlightedLinks: [
                {
                    name: "Søk på adresse",
                    link: "/internett/sok-adresse/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Kjøp bredbånd til hytta",
                    link: "/internett/tradlost-bredband-hytte/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Kampanjer",
                    link: "/kampanje/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Problemer med internett",
                    link: "/internett/internett-problemer/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Rutere og innstillinger",
                    link: "/internett/wifi/rutere/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "WiFi-tips",
                    link: "/internett/wifi/wifi-tips/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
            links: [
                {
                    name: "Fast bredbånd",
                    link: "/internett/bredband/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Trådløst bredbånd",
                    link: "/internett/tradlost-bredband/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Mobilt bredbånd",
                    link: "/internett/mobilt-bredband/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "WiFi og hjemmenett",
                    link: "/internett/wifi/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Driftsmeldinger",
                    link: "/hjelp/driftsmeldinger/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Speedtest",
                    link: "/internett/speedtest/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Hjelp internett",
                    link: "/internett/hjelp/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Alt om internett",
                    link: "/internett/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
        },
        {
            name: "Trygghet",
            link: "/trygghet/",
            appKey: APP_KEYS["open-pages"],
            highlightedLinks: [
                {
                    name: "Telia Trygg",
                    link: "/trygghet/tjenester/telia-trygg/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Mobilforsikring",
                    link: "/trygghet/tjenester/mobilforsikring/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Telia Sky",
                    link: "/trygghet/tjenester/telia-sky/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Trygg mobil for barn",
                    link: "/trygghet/trygg-pa-skjerm/trygg-mobil-til-barn/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Ta styring over skjermtiden",
                    link: "/trygghet/skjermhelse/skjermtid/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Falske nettbutikker",
                    link: "/trygghet/svindel/falske-nettbutikker/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "ID-tyveri",
                    link: "/trygghet/svindel/id-tyveri/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Kurs: Trygg på skjerm",
                    link: "/trygghet/trygg-pa-skjerm/kurs/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
            links: [
                {
                    name: "Tjenester",
                    link: "/trygghet/tjenester/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Svindel",
                    link: "/trygghet/svindel/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Skjermhelse",
                    link: "/trygghet/skjermhelse/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Trygg på skjerm",
                    link: "/trygghet/trygg-pa-skjerm/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Aktuelt",
                    link: "/trygghet/aktuelt/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Hjelp trygghet",
                    link: "/trygghet/hjelp/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Alt om trygghet",
                    link: "/trygghet/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
        },
        {
            name: "Hjelp",
            link: "/hjelp/",
            appKey: APP_KEYS["open-pages"],
            highlightedLinks: [
                {
                    name: "SIM, PIN og PUK",
                    link: "/mobilabonnement/sim-kort/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Driftsmeldinger",
                    link: "/hjelp/driftsmeldinger/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Dekningskart",
                    link: "/nett/dekning/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Returnere rutere og TV-bokser",
                    link: "/hjelp/retur-av-utstyr/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Last ned apper",
                    link: "/hjelp/apper/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Se din faktura",
                    link: "/minside/faktura/",
                    appKey: APP_KEYS["my-pages"],
                },
            ],
            links: [
                {
                    name: "Hjelp mobil",
                    link: "/mobilabonnement/hjelp/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Hjelp TV",
                    link: "/tv/hjelp/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Hjelp internett",
                    link: "/internett/hjelp/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Hjelp trygghet",
                    link: "/trygghet/hjelp/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Faktura",
                    link: "/hjelp/faktura/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Flytting",
                    link: "/hjelp/flytting/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Finn butikk",
                    link: "/hjelp/butikker-og-forhandlere/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Alt om hjelp",
                    link: "/hjelp/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
        },
    ],
} as const satisfies Link;

export const BUSINESS_LINKS = {
    name: "Bedrift",
    link: "/bedrift/",
    appKey: APP_KEYS["open-pages"],
    highlightedLinks: [],
    links: [
        {
            name: "Logo",
            link: "/bedrift/",
            appKey: APP_KEYS["open-pages"],
            links: [],
            highlightedLinks: [],
        },
        {
            name: "Produkter og tjenester",
            link: "/bedrift/produkter-og-tjenester/",
            appKey: APP_KEYS["open-pages"],
            links: [
                {
                    name: "Mobil og utstyr",
                    link: "/bedrift/mobil-og-utstyr/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Telefoni og kundebetjening",
                    link: "/bedrift/telefoni-og-kundebetjening/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Internett og Bredbånd",
                    link: "/bedrift/internett-og-bredband/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Nettverk",
                    link: "/bedrift/nettverk/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Sikkerhet",
                    link: "/bedrift/sikkerhet/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Sky",
                    link: "/bedrift/sky/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "IoT",
                    link: "/bedrift/iot/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "IT-tjenester",
                    link: "/bedrift/it-tjenester-for-bedrifter/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
            highlightedLinks: [
                {
                    name: "Hjelpesider mobil",
                    link: "/bedrift/kundeservice/mobil/#selvhjelp-mobil",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Hjelpesider datakom",
                    link: "/bedrift/kundeservice/datakommunikasjon/#selvhjelp-datakom",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Bransjer",
                    link: "/bedrift/teliabyen/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Be om tilbud",
                    link: "/bedrift/produkter-og-tjenester/be-om-tilbud/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Alle produkter og tjenester",
                    link: "/bedrift/produkter-og-tjenester/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
        },
        {
            name: "Sikkerhet",
            link: "/bedrift/sikkerhet/",
            appKey: APP_KEYS["open-pages"],
            links: [
                {
                    name: "Trygge ansatte",
                    link: "/bedrift/sikkerhet/trygge-ansatte/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Sikre skytjenester",
                    link: "/bedrift/sikkerhet/sikre-skytjenester/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Sikre nettverk",
                    link: "/bedrift/sikkerhet/sikre-nettverket/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Alt om sikkerhet",
                    link: "/bedrift/sikkerhet/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
            highlightedLinks: [
                {
                    name: "Sikkerhetsrådgivning",
                    link: "/bedrift/sikkerhet/sikkerhetsradgivning/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Sikkerhetsløsninger",
                    link: "/bedrift/sikkerhet/sikkerhetslosninger/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Proaktiv support og drift",
                    link: "/bedrift/sikkerhet/proaktiv-support-og-drift/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Gjenoppretting og læring",
                    link: "/bedrift/sikkerhet/gjenoppretting-og-laring/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Be om tilbud",
                    link: "/bedrift/produkter-og-tjenester/be-om-tilbud/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Bransjer",
                    link: "/bedrift/teliabyen/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
        },
        {
            name: "Kundehistorier",
            link: "/bedrift/produkter-og-tjenester/kundehistorier/",
            appKey: APP_KEYS["open-pages"],
            links: [
                {
                    name: "Innovasjon med 5G",
                    link: "/bedrift/produkter-og-tjenester/kundehistorier/#inno",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Helhetlige løsninger",
                    link: "/bedrift/produkter-og-tjenester/kundehistorier/#helh",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Sikkerhet i alle ledd",
                    link: "/bedrift/produkter-og-tjenester/kundehistorier/#sikk",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Smart teknologi",
                    link: "/bedrift/produkter-og-tjenester/kundehistorier/#smar",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Alt om kundehistorier",
                    link: "/bedrift/produkter-og-tjenester/kundehistorier/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
            highlightedLinks: [
                {
                    name: "Bransjer",
                    link: "/bedrift/teliabyen/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Be om tilbud",
                    link: "/bedrift/produkter-og-tjenester/be-om-tilbud/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
        },
        {
            name: "Innovasjon",
            link: "/bedrift/innovasjon-og-startup/",
            appKey: APP_KEYS["open-pages"],
            links: [
                {
                    name: "Innovasjonsløsninger",
                    link: "/bedrift/innovasjon-og-startup/#losninger",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Telia Startup",
                    link: "/bedrift/innovasjon-og-startup/telia-startup/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Våre startups",
                    link: "/bedrift/innovasjon-og-startup/vare-startups/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Alt om Innovasjon",
                    link: "/bedrift/innovasjon-og-startup/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
            highlightedLinks: [
                {
                    name: "Kundehistorier",
                    link: "/bedrift/produkter-og-tjenester/kundehistorier/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Bransjer",
                    link: "/bedrift/teliabyen/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Be om tilbud",
                    link: "/bedrift/produkter-og-tjenester/be-om-tilbud/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "5G",
                    link: "/bedrift/5g-bedrift/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "IoT",
                    link: "/bedrift/iot/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "IT-tjenester",
                    link: "/bedrift/it-tjenester-for-bedrifter/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
        },
        {
            name: "Nettbutikk",
            link: "/bedrift/mobiltelefoner/",
            appKey: APP_KEYS["web-shop"],
            links: [
                {
                    name: "Be om tilbud",
                    link: "/bedrift/produkter-og-tjenester/be-om-tilbud/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Mobilabonnement",
                    link: "/bedrift/mobilabonnement/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Mobiltelefoner",
                    link: "/bedrift/mobiltelefoner/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Mobilt bredbånd",
                    link: "/bedrift/mobilt-bredband/",
                    appKey: APP_KEYS["web-shop"],
                },
            ],
            highlightedLinks: [
                {
                    name: "Telia Ubegrenset",
                    link: "/bedrift/mobilabonnement/telia-ubegrenset/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Telia Dobbel",
                    link: "/bedrift/mobilabonnement/telia-dobbel/",
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Data Boost for ansatte",
                    link: "/bedrift/mobil-og-utstyr/data-boost-b2b/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Ekstra datapakke for ansatte",
                    link: "/bedrift/mobil-og-utstyr/ekstra-datapakker/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
        },

        {
            name: "Hjelp",
            link: "/bedrift/kundeservice/",
            appKey: APP_KEYS["open-pages"],
            links: [
                {
                    name: "Hjelpesider mobil og telefoni",
                    link: "/bedrift/kundeservice/mobil/#selvhjelp-mobil",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Hjelpesider datakommunikasjon",
                    link: "/bedrift/kundeservice/datakommunikasjon/#selvhjelp-datakom",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Kundeservice mobil og telefoni",
                    link: "/bedrift/kundeservice/mobil/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Kundeservice datakommunikasjon",
                    link: "/bedrift/kundeservice/datakommunikasjon/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Kundeservice TV og digital skilting",
                    link: "/bedrift/kundeservice/tv-og-digital-skilting/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Alt om hjelp",
                    link: "/bedrift/kundeservice/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
            highlightedLinks: [
                {
                    name: "SIM, PIN og PUK",
                    link: "/mobilabonnement/sim-kort/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Dekningskart",
                    link: "/nett/dekning/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Faktura",
                    link: "/bedrift/kundeservice/mobil/faktura-hjelpesider/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Eierskifte",
                    link: "/bedrift/mobil-og-utstyr/bytte-eier-pa-bedriftsabonnement/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Min Bedrift",
                    link: "https://beta.minbedrift.telia.no/",
                    appKey: APP_KEYS["my-business"],
                },
                {
                    name: "Min Portal",
                    link: "https://minportal.telia.no/",
                    appKey: APP_KEYS["my-portal"],
                },
                {
                    name: "Sentralbord",
                    link: "https://teliasmartconnect.com/",
                    appKey: APP_KEYS["open-pages"],
                },
                {
                    name: "Ny kunde",
                    link: "/bedrift/velkommen/",
                    appKey: APP_KEYS["open-pages"],
                },
            ],
        },
    ],
} as const satisfies Link;

export const BUSINESS_UKKO_LINKS = {
    name: "Bedrift",
    link: "/bedrift/",
    appKey: APP_KEYS["open-pages"],
    highlightedLinks: [],
    links: [
        {
            name: "Logo",
            link: "https://device.teliacompany.com/",
            appKey: APP_KEYS["open-pages"],
            links: [],
            highlightedLinks: [],
        },
        {
            name: "Hjem",
            link: "https://device.teliacompany.com/",
            appKey: APP_KEYS["open-pages"],
            links: [],
            highlightedLinks: [],
        },
    ],
} as const satisfies Link;

export const MDU_LINKS = {
    name: "Borettslag",
    link: "/borettslag/",
    appKey: APP_KEYS["open-pages"],
    highlightedLinks: [],
    links: [
        {
            name: "Logo",
            link: "/borettslag/",
            appKey: APP_KEYS["open-pages"],
            links: [],
            highlightedLinks: [],
        },
        {
            name: "Beboer",
            link: "/borettslag/beboer/",
            appKey: APP_KEYS["open-pages"],
            links: [],
            highlightedLinks: [],
        },
        {
            name: "Styre",
            link: "/borettslag/styre/",
            appKey: APP_KEYS["open-pages"],
            links: [],
            highlightedLinks: [],
        },
        {
            name: "Utbygger",
            link: "/borettslag/utbygger/",
            appKey: APP_KEYS["open-pages"],
            links: [],
            highlightedLinks: [],
        },
    ],
} as const satisfies Link;

// Default link configurations
export const DEFAULT_PRIVATE_LINKS = PRIVATE_LINKS;
export const DEFAULT_BUSINESS_LINKS = BUSINESS_LINKS;
export const DEFAULT_BUSINESS_UKKO_LINKS = BUSINESS_UKKO_LINKS;
export const DEFAULT_MDU_LINKS = MDU_LINKS;

export type CustomLinksConfig = {
    businessLinks?: Link;
    businessUkkoLinks?: Link;
    mduLinks?: Link;
    privateLinks?: Link;
};

export const RETAILER_LINKS = {
    name: "Nexus",
    link: PAGES.RETAILER.URL,
    appKey: APP_KEYS["web-shop"],
    highlightedLinks: [],
    links: [
        {
            name: "Logo",
            link: PAGES.RETAILER.NEWORDER.URL,
            appKey: APP_KEYS["web-shop"],
            links: [],
            highlightedLinks: [],
        },
        {
            name: "Ordre",
            link: PAGES.RETAILER.NEWORDER.URL,
            appKey: APP_KEYS["web-shop"],
            links: [
                {
                    name: "Start ny ordre",
                    link: PAGES.RETAILER.NEWORDER.URL,
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Ordreoversikt",
                    link: PAGES.RETAILER.ORDERS_OVERVIEW.URL,
                    appKey: APP_KEYS["web-shop"],
                },
            ],
        },
        {
            name: "Salgsrapporter",
            link: PAGES.RETAILER.SALES_REPORTS.SALES_TODAY.URL,
            appKey: APP_KEYS["web-shop"],
            links: [
                {
                    name: "Salg i dag",
                    link: PAGES.RETAILER.SALES_REPORTS.SALES_TODAY.URL,
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Salg siste måned",
                    link: PAGES.RETAILER.SALES_REPORTS.SALES_LAST_MONTH.URL,
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Provisjon",
                    link: PAGES.RETAILER.SALES_REPORTS.COMMISSION.URL,
                    appKey: APP_KEYS["web-shop"],
                },
            ],
        },
        {
            name: "Salgsstøtte",
            link: PAGES.RETAILER.SALES_SUPPORT.NEWS.URL,
            appKey: APP_KEYS["web-shop"],
            links: [
                {
                    name: "Nyheter",
                    link: PAGES.RETAILER.SALES_SUPPORT.NEWS.URL,
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Presentasjoner",
                    link: PAGES.RETAILER.SALES_SUPPORT.PRESENTATIONS.URL,
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Skjema",
                    link: PAGES.RETAILER.SALES_SUPPORT.FORMS.URL,
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Provisjonsbulletin",
                    link: PAGES.RETAILER.SALES_SUPPORT.COMMISSION_BULLETIN.URL,
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Rutiner",
                    link: PAGES.RETAILER.SALES_SUPPORT.PROCEDURES.URL,
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Dekningskart",
                    link: PAGES.RETAILER.SALES_SUPPORT.COVERAGE_MAP.URL,
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Bestill SIM",
                    link: PAGES.RETAILER.SALES_SUPPORT.ORDER_SIM.URL,
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Eierskifteskjema",
                    link: PAGES.RETAILER.SALES_SUPPORT.OWNERSHIP_TRANSFER.URL,
                    appKey: APP_KEYS["web-shop"],
                },
            ],
        },
        {
            name: "Administrasjon",
            link: PAGES.RETAILER.ADMIN.SHOP.URL,
            appKey: APP_KEYS["web-shop"],
            links: [
                {
                    name: "Butikk",
                    link: PAGES.RETAILER.ADMIN.SHOP.URL,
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Selgere",
                    link: PAGES.RETAILER.ADMIN.SELLERS.URL,
                    appKey: APP_KEYS["web-shop"],
                },
                {
                    name: "Velg butikk",
                    link: PAGES.RETAILER.SHOPS.URL,
                    appKey: APP_KEYS["web-shop"],
                },
            ],
        },
    ],
} as const satisfies Link;

export type GetDesktopLinksProps = {
    customLinks?: CustomLinksConfig;
    isD2DOrTMDealer?: boolean;
    isRetailer?: boolean;
    ukko?: boolean;
};

export const getDesktopLinks = ({ ukko, customLinks, isRetailer, isD2DOrTMDealer }: GetDesktopLinksProps = {}) => {
    const privateLinks = customLinks?.privateLinks || DEFAULT_PRIVATE_LINKS;
    const businessLinks = ukko
        ? customLinks?.businessUkkoLinks || DEFAULT_BUSINESS_UKKO_LINKS
        : customLinks?.businessLinks || DEFAULT_BUSINESS_LINKS;
    const mduLinks = customLinks?.mduLinks || DEFAULT_MDU_LINKS;

    if (isRetailer) {
        return {
            privateLinks: isD2DOrTMDealer
                ? filterRetailerLinks(RETAILER_LINKS, D2D_AND_TM_HIDDEN_LINKS)
                : RETAILER_LINKS,
        };
    }

    return { privateLinks, businessLinks, mduLinks };
};
